/**
 * Register Page
 *
 * Multi-step registration flow:
 * 1. Role Selection (Buyer/Seller)
 * 2. Corporate Email Input + Company Detection
 * 3. Personal Profile + Sub-organization
 * 4. Password Creation
 * 5. Email Verification
 * 6. Success
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2,
  FlaskConical,
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Mail,
  Loader2,
  PartyPopper,
  Users,
  Plus,
  User,
  Gift
} from 'lucide-react';
import { validateCorporateEmail, validatePassword } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import api, { companiesAPI } from '../../services/api';
import PasswordInput from '../../components/auth/PasswordInput';
import EmailVerificationModal from '../../components/auth/EmailVerificationModal';

// Step indicators
const STEPS = [
  { id: 1, name: 'Role' },
  { id: 2, name: 'Email' },
  { id: 3, name: 'Profile' },
  { id: 4, name: 'Password' },
];

// Title options
const TITLE_OPTIONS = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// Email provider detection
const EMAIL_PROVIDERS = {
  'gmail.com': { name: 'Gmail', url: 'https://mail.google.com' },
  'googlemail.com': { name: 'Gmail', url: 'https://mail.google.com' },
  'outlook.com': { name: 'Outlook', url: 'https://outlook.live.com' },
  'hotmail.com': { name: 'Outlook', url: 'https://outlook.live.com' },
  'live.com': { name: 'Outlook', url: 'https://outlook.live.com' },
  'yahoo.com': { name: 'Yahoo Mail', url: 'https://mail.yahoo.com' },
  'icloud.com': { name: 'iCloud Mail', url: 'https://www.icloud.com/mail' },
  'qq.com': { name: 'QQ Mail', url: 'https://mail.qq.com' },
  '163.com': { name: '163 Mail', url: 'https://mail.163.com' },
  '126.com': { name: '126 Mail', url: 'https://mail.126.com' },
};

function getEmailProviderUrl(email) {
  const domain = email.split('@')[1]?.toLowerCase();
  return EMAIL_PROVIDERS[domain]?.url || `https://${domain}`;
}

function getEmailProviderName(email) {
  const domain = email.split('@')[1]?.toLowerCase();
  return EMAIL_PROVIDERS[domain]?.name || 'Email';
}

export function Register() {
  const navigate = useNavigate();
  const { signUp, verifyOtp, resendVerification } = useAuth();

  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [userType, setUserType] = useState(null); // 'buyer' or 'seller'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyInfo, setCompanyInfo] = useState(null);

  // Company/Organization state
  const [existingCompany, setExistingCompany] = useState(null);
  const [joinExisting, setJoinExisting] = useState(null); // null = not decided, true = join, false = create new
  const [companyName, setCompanyName] = useState('');

  // Personal profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [title, setTitle] = useState('');

  // Sub-organization state
  const [selectedSubOrg, setSelectedSubOrg] = useState('');
  const [newSubOrgName, setNewSubOrgName] = useState('');
  const [showNewSubOrgInput, setShowNewSubOrgInput] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  // Step 1: Role Selection
  const handleRoleSelect = (role) => {
    setUserType(role);
    setError(null);
    setCurrentStep(2);
  };

  // Step 2: Email Validation + Company Check
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setExistingCompany(null);
    setJoinExisting(null);

    try {
      // First validate corporate email
      const result = await validateCorporateEmail(email, userType);

      if (!result.valid) {
        setError(result.error);
        if (result.showContactAdmin) {
          setError(
            <div>
              <p className="font-medium">{result.error}</p>
              <p className="mt-2 text-sm">
                {result.foundInBuyers || result.foundInSellers ? (
                  <>
                    Your company is registered for {result.foundInBuyers ? 'Buyer' : 'Seller'} accounts.
                    <button
                      onClick={() => {
                        setUserType(result.foundInBuyers ? 'buyer' : 'seller');
                        setError(null);
                      }}
                      className="ml-1 text-blue-600 hover:underline"
                    >
                      Switch to {result.foundInBuyers ? 'Buyer' : 'Seller'}?
                    </button>
                  </>
                ) : (
                  <>
                    Contact us at{' '}
                    <a href="mailto:admin@pharmunity.io" className="text-blue-600 hover:underline">
                      admin@pharmunity.io
                    </a>{' '}
                    to add your company.
                  </>
                )}
              </p>
            </div>
          );
        }
        return;
      }

      setCompanyInfo(result);

      // Check if company already exists in our organization system
      try {
        const { data: companyCheckResult } = await companiesAPI.checkDomain(email);
        if (companyCheckResult?.data?.exists) {
          setExistingCompany(companyCheckResult.data.company);
          setCompanyName(companyCheckResult.data.company.name);
        } else {
          // Use suggested name for new company
          setCompanyName(companyCheckResult?.data?.suggested_name || result.company?.company_name || '');
        }
      } catch (checkErr) {
        // If check fails, continue without org features
        console.warn('Company check failed:', checkErr);
        setCompanyName(result.company?.company_name || '');
      }

      setCurrentStep(3);
    } catch (err) {
      setError('Failed to validate email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Profile + Organization
  const handleProfileSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }

    // If existing company but user hasn't decided
    if (existingCompany && joinExisting === null) {
      setError('Please choose whether to join the existing team or create a separate account');
      return;
    }

    setCurrentStep(4);
  };

  // Step 4: Password Creation
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate password
    const { isValid } = validatePassword(password);
    if (!isValid) {
      setError('Please meet all password requirements.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare registration data with profile info
      const registrationData = {
        ...companyInfo,
        // Personal profile
        first_name: firstName,
        last_name: lastName,
        preferred_name: preferredName || null,
        title: title || null,
        // Organization
        company_name: companyName,
        join_existing_company: joinExisting,
        existing_company_id: existingCompany?.id || null,
        sub_organization_name: showNewSubOrgInput ? newSubOrgName : selectedSubOrg || null,
      };

      const { error: signUpError } = await signUp(email, password, userType, registrationData);

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setError(
            <div>
              <p>This email is already registered.</p>
              <Link to="/login" className="text-blue-600 hover:underline">
                Sign in instead?
              </Link>
            </div>
          );
        } else {
          setError(signUpError.message);
        }
        return;
      }

      // Go to email confirmation page
      setCurrentStep(5);
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 5: Email Verification
  const handleVerify = async (code) => {
    setIsVerifying(true);
    setVerificationError(null);

    try {
      const { error } = await verifyOtp(email, code, userType, companyInfo);

      if (error) {
        setVerificationError(error.message || 'Invalid verification code');
        return;
      }

      // Success - move to step 6
      setShowVerificationModal(false);
      setCurrentStep(6);
    } catch (err) {
      setVerificationError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    await resendVerification(email);
  };

  // Step 6: Success - redirect
  const handleGoToDashboard = () => {
    if (userType === 'buyer') {
      navigate('/buyer/search');
    } else {
      navigate('/seller/dashboard');
    }
  };

  // Go back to previous step
  const handleBack = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-md mx-auto">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-5 h-5" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          {/* Progress Indicator */}
          {currentStep < 6 && (
            <div className="mb-8">
              <div className="flex justify-between items-center">
                {STEPS.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        step.id < currentStep
                          ? 'bg-green-500 text-white'
                          : step.id === currentStep
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {step.id < currentStep ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        step.id
                      )}
                    </div>
                    {index < STEPS.length - 1 && (
                      <div
                        className={`w-8 h-1 mx-1 ${
                          step.id < currentStep ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Role Selection */}
          {currentStep === 1 && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center mx-auto mb-4 overflow-hidden shadow-sm">
                <img
                  src="/logo.png"
                  alt="Pharmunity"
                  className="h-18 w-18 object-cover scale-[1.35]"
                />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to Pharmunity
              </h1>
              <p className="text-gray-600 mb-8">I am a...</p>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleRoleSelect('buyer')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                    <Building2 className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">BUYER</h3>
                  <p className="text-sm text-gray-500">
                    Looking to license IN new assets
                  </p>
                </button>

                <button
                  onClick={() => handleRoleSelect('seller')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group"
                >
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
                    <FlaskConical className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">SELLER</h3>
                  <p className="text-sm text-gray-500">
                    Looking to license OUT our assets
                  </p>
                </button>
              </div>

              <p className="mt-8 text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          )}

          {/* Step 2: Email Input */}
          {currentStep === 2 && (
            <div>
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Enter your corporate email
              </h1>
              <p className="text-gray-600 mb-6">
                You're registering as a{' '}
                <span className="font-medium capitalize">{userType}</span>
              </p>

              <form onSubmit={handleEmailSubmit}>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Corporate Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Only verified corporate domains are accepted. Personal emails (gmail, yahoo, etc.) are not allowed.
                  </p>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 3: Profile + Organization */}
          {currentStep === 3 && (
            <div>
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              {/* Company Detected */}
              {companyInfo?.company && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Corporate email verified</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Company: <strong>{companyInfo.company.company_name}</strong>
                  </p>
                </div>
              )}

              {/* Existing Company Warning */}
              {existingCompany && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-800">
                      {existingCompany.name} already has {existingCompany.member_count} team member(s)
                    </span>
                  </div>
                  <p className="text-sm text-blue-700 mb-4">
                    Would you like to join the existing team or create a separate account?
                  </p>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setJoinExisting(true)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                        joinExisting === true
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">Join {existingCompany.name} team</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1 ml-6">
                        Share company profile, see colleague activity
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setJoinExisting(false)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                        joinExisting === false
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-600" />
                        <span className="font-medium">Create separate account</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1 ml-6">
                        For different division or independent work
                      </p>
                    </button>
                  </div>
                </div>
              )}

              <h1 className="text-2xl font-bold text-gray-900 mb-6">
                Your profile
              </h1>

              <form onSubmit={handleProfileSubmit}>
                {/* Personal Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Jane"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Smith"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Name
                    </label>
                    <input
                      type="text"
                      value={preferredName}
                      onChange={(e) => setPreferredName(e.target.value)}
                      placeholder="Optional"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <select
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select...</option>
                      {TITLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sub-organization (only if joining existing or has sub-orgs) */}
                {(joinExisting || existingCompany?.sub_organizations?.length > 0) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department / Division
                    </label>

                    {existingCompany?.sub_organizations?.length > 0 && !showNewSubOrgInput && (
                      <div className="space-y-2 mb-2">
                        {existingCompany.sub_organizations.map((subOrg) => (
                          <button
                            key={subOrg.id}
                            type="button"
                            onClick={() => setSelectedSubOrg(subOrg.name)}
                            className={`w-full p-2 rounded-lg border text-left transition-colors ${
                              selectedSubOrg === subOrg.name
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            {subOrg.name}
                          </button>
                        ))}
                      </div>
                    )}

                    {!showNewSubOrgInput ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewSubOrgInput(true);
                          setSelectedSubOrg('');
                        }}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                        Add new department
                      </button>
                    ) : (
                      <div>
                        <input
                          type="text"
                          value={newSubOrgName}
                          onChange={(e) => setNewSubOrgName(e.target.value)}
                          placeholder="e.g., Oncology, China, BD Team"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {existingCompany?.sub_organizations?.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowNewSubOrgInput(false);
                              setNewSubOrgName('');
                            }}
                            className="text-sm text-gray-500 hover:text-gray-700 mt-1"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show sub-org input for new companies too */}
                {!existingCompany && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department / Division (Optional)
                    </label>
                    <input
                      type="text"
                      value={newSubOrgName}
                      onChange={(e) => setNewSubOrgName(e.target.value)}
                      placeholder="e.g., Oncology, China, BD Team"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Helps colleagues from the same company find the right team
                    </p>
                  </div>
                )}

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          )}

          {/* Step 4: Password Creation */}
          {currentStep === 4 && (
            <div>
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              {/* Summary */}
              <div className="p-4 bg-gray-50 rounded-lg mb-6">
                <h3 className="font-medium text-gray-900 mb-2">Registration Summary</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Name:</strong> {title && `${title} `}{firstName} {lastName}</p>
                  <p><strong>Company:</strong> {companyName}{(selectedSubOrg || newSubOrgName) && ` - ${selectedSubOrg || newSubOrgName}`}</p>
                  <p><strong>Role:</strong> <span className="capitalize">{userType}</span></p>
                  {existingCompany && joinExisting && (
                    <p className="text-blue-600">Joining existing team</p>
                  )}
                </div>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-6">
                Create your password
              </h1>

              <form onSubmit={handlePasswordSubmit}>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  label="Password"
                  showRequirements={true}
                  className="mb-4"
                />

                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  label="Confirm Password"
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  className="mb-6"
                />

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                  className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 5: Check Email */}
          {currentStep === 5 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-blue-600" />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Check your email
              </h1>
              <p className="text-gray-600 mb-4">
                We sent a confirmation link to:
              </p>
              <p className="font-medium text-gray-900 mb-6 bg-gray-100 py-2 px-4 rounded-lg inline-block">
                {email}
              </p>
              <p className="text-gray-600 mb-8">
                Click the link in the email to verify your account and complete registration.
              </p>

              <div className="space-y-4">
                <a
                  href={getEmailProviderUrl(email)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Open {getEmailProviderName(email)}
                </a>

                <p className="text-sm text-gray-500">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={handleResendCode}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    resend confirmation email
                  </button>
                </p>

                <p className="text-sm text-gray-400">
                  Already verified?{' '}
                  <Link to="/login" className="text-blue-600 hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* Step 6: Success */}
          {currentStep === 6 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <PartyPopper className="w-10 h-10 text-green-600" />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to Pharmunity!
              </h1>
              <p className="text-gray-600 mb-2">
                Hi {preferredName || firstName}! You're registered as a{' '}
                <span className="font-medium capitalize">{userType}</span>.
              </p>
              <p className="text-gray-600 mb-8">
                Company: <span className="font-medium">{companyName}</span>
                {(selectedSubOrg || newSubOrgName) && (
                  <> - {selectedSubOrg || newSubOrgName}</>
                )}
              </p>

              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Verification Modal */}
      {showVerificationModal && (
        <EmailVerificationModal
          email={email}
          onVerify={handleVerify}
          onResend={handleResendCode}
          isVerifying={isVerifying}
          error={verificationError}
          onClose={() => setShowVerificationModal(false)}
        />
      )}
    </div>
  );
}

export default Register;
